import styled from '@emotion/styled'
import { TextLink } from '../../../../../../typography/TextLink/TextLink'
import { colors } from '../../../../../../__styling/settings/colors'
import { zIndex } from '../../../../../../__styling/settings/z-index'
import { calcRem } from '../../../../../../__styling/style-functions/calc-rem'
import {
    fluidTypography,
    miniDivider,
    transition,
} from '../../../../../../__styling/style-mixins'
import { themedValue } from '../../../../../../__styling/themed-value'
import { Theme } from '../../../../../../__styling/themes'

export const StyledBreachWallContent = styled('div')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    position: 'relative',
    zIndex: zIndex.common.regWall.box,
    fontFamily: theme.fonts.subscriber.text,
    color: colors.white,
    paddingTop: calcRem(theme.margins.lg),
    paddingBottom: calcRem(theme.margins.md),
    background: themedValue(theme, {
        thewest: colors.thewest.westblue,
        perthnow: colors.perthnow.greyBasalt90,
        sevennews: colors.sevennews.charade,
    }),
}))

export const StyledRegistrationInfo = styled('div')(({ theme }) => ({
    flexBasis: 250,
    flexGrow: 1,
    margin: calcRem(0, theme.margins.md),
}))

const fluidTitle = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(0, 1000, 24, 30),
        perthnow: fluidTypography(0, 1020, 24, 30),
        thewest: fluidTypography(0, 1220, 24, 38),
    })

export const StyledTitle = styled('h2')(({ theme }) => ({
    position: 'relative',
    margin: calcRem(0, 0, theme.margins.xxl, 0),
    fontWeight: 700,
    lineHeight: 1.267,
    ...fluidTitle(theme),

    // Pseudo element divider
    ...miniDivider({ theme: theme, dividerColor: colors.black }),
}))

export const StyledMessage = styled('div')(({ theme }) => ({
    marginBottom: calcRem(theme.margins.xl),

    p: {
        margin: 0,
    },
}))

export const StyledPackage = styled('div')(({ theme }) => ({
    flexGrow: 1,
    flexBasis: 400,
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'space-around',
    margin: calcRem(0, theme.margins.md),

    '@media screen and (max-width: 374px)': {
        flexWrap: 'wrap',

        'div:last-of-type': {
            marginLeft: 0,
        },
    },
}))

export const StyledButton = styled('button')(({ theme }) => ({
    display: 'inline-block',
    border: `1px solid ${colors.white}`,
    padding: calcRem(theme.margins.sm, theme.margins.md),
    background: 'none',
    color: colors.white,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontWeight: 700,
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        backgroundColor: colors.white,
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.blueKyanite,
            sevennews: colors.sevennews.red,
        }),
    },

    '&:focus': {
        backgroundColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
        }),
        borderColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
        }),
        color: 'currentColor',
        outline: 'none',
        textDecoration: 'underline',
    },
}))

export const StyledPrintDigTitle = styled(StyledTitle)({
    fontSize: calcRem(23), // DPT-3294: campaign requires specific sizing
})

export const StyledTerms = styled('p')(({ theme }) => ({
    color: colors.white,
    margin: 0,
    marginTop: calcRem(theme.margins.sm),
    fontSize: calcRem(11),
}))

export const StyledLiveTrailerWrap = styled('div')({
    margin: `${calcRem(30)} auto`,
    boxShadow: '0 0 16px rgba(0,0,0,0.5)',
})

export const StyledHelpMessage = styled('p')(({ theme }) => ({
    margin: calcRem(theme.margins.sm, 0),
}))

export const StyledTextLink = styled(TextLink)<{ reverseColor: boolean }>(
    ({ reverseColor }) => ({
        color: reverseColor ? colors.white : undefined,
        textDecoration: 'none',

        '&:focus': {
            textDecoration: 'underline',
        },
    }),
)
